/**
 * Per-turn wall-clock budget (PROTOTYPER_STABILITY_TECH_PLAN.md P6.5, delivered in P6.4d).
 *
 * One turn — its first stream, continuation rounds and automatic fix attempts — gets 25 minutes.
 * Past that the stream is stopped and the version waits for the founder (reason `timeout`),
 * so a build that never passes cannot keep running for the rest of the session.
 *
 * Kept dependency-free so it stays unit-testable in the node test env.
 */

export const TURN_BUDGET_MS = 25 * 60_000;
/** How often a running turn re-checks its budget between other events. */
export const TURN_BUDGET_CHECK_MS = 30_000;

export interface TurnBudgets {
  /** Starts the turn's clock the first time; later rounds and fix attempts keep it. */
  start(llmInteractionId: string, now: number): void;
  /** Starts the clock again (the founder asked for more: "Try another fix"). */
  restart(llmInteractionId: string, now: number): void;
  expired(llmInteractionId: string, now: number): boolean;
  forget(llmInteractionId: string): void;
}

export function createTurnBudgets(budgetMs: number = TURN_BUDGET_MS): TurnBudgets {
  const startedAt = new Map<string, number>();
  return {
    start(llmInteractionId, now) {
      if (!startedAt.has(llmInteractionId)) startedAt.set(llmInteractionId, now);
    },
    restart(llmInteractionId, now) {
      startedAt.set(llmInteractionId, now);
    },
    expired(llmInteractionId, now) {
      const started = startedAt.get(llmInteractionId);
      return started !== undefined && now - started >= budgetMs;
    },
    forget(llmInteractionId) {
      startedAt.delete(llmInteractionId);
    },
  };
}

export const TURN_BUDGET_SIGNATURE = 'turn_budget_exceeded';

/** Shown when the versions API cannot record the timeout for the card. */
export const TURN_BUDGET_MESSAGE =
  'This build ran for 25 minutes without passing, so it was stopped.';
